import { createFileRoute } from '@tanstack/react-router';
import { ThreadWindow } from '@/components/chats/thread-window';
import { useChatStore } from '@/stores';
import { useEffect } from 'react';
import { useRoomMessages } from '@/hooks/chats/use-room-messages';

export const Route = createFileRoute('/(app)/productivity/chats/$threadId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { threadId } = Route.useParams();

  // Subscribe to the specific thread via a selector so the component re-renders
  // when its messages or metadata change. Using getThread(threadId) as a function
  // call gets memoized by React Compiler (stable inputs) and returns stale data.
  const thread = useChatStore((state) =>
    state.threads.find((t) => t.meta.room_id === threadId),
  );
  const setSelectedThread = useChatStore((state) => state.setSelectedThread);

  // Windowed message history (full-history paging) — this is the display source.
  const room = useRoomMessages(threadId);

  // Prefer the live query window; fall back to the store's persisted newest
  // slice so the thread paints instantly on open/reload before page 1 lands.
  const messages = room.messages.length > 0 ? room.messages : (thread?.messages ?? []);

  useEffect(() => {
    setSelectedThread(threadId);
    return () => setSelectedThread(null);
  }, [threadId, setSelectedThread]);

  // Only block on a true cold start — nothing cached and the first page still
  // in flight. Otherwise render immediately from whatever we have.
  if (!thread && messages.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {room.isLoading
            ? 'Loading messages…'
            : `Loading messages for thread: ${threadId}`}
        </p>
      </div>
    );
  }

  return (
    <ThreadWindow
      messages={messages}
      threadId={threadId}
      title={thread?.meta.title}
      onLoadOlder={room.fetchOlder}
      hasOlder={room.hasOlder}
      isLoadingOlder={room.isLoadingOlder}
      onLoadNewer={room.fetchNewer}
      hasNewer={room.hasNewer}
      onResetToLatest={room.resetToLatest}
      isLoadingMessages={room.isLoading && messages.length === 0}
    />
  );
}
